import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CalendarDays, Clock, Bell, Check, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function relativeTime(iso, now) {
  const dt = new Date(iso);
  const diffMs = dt - now;
  const diffMin = Math.round(diffMs / 60000);
  const absMin = Math.abs(diffMin);
  if (absMin < 1) return diffMin >= 0 ? 'nu' : 'nu';
  if (absMin < 60) return diffMin > 0 ? `om ${absMin} min` : `${absMin} min sedan`;
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) return diffHr > 0 ? `om ${diffHr} h` : `${Math.abs(diffHr)} h sedan`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return 'imorgon';
  if (diffDay === -1) return 'igår';
  if (diffDay > 1 && diffDay <= 7) return `om ${diffDay} dagar`;
  return dt.toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' });
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}

export default function UpcomingEventsCard({ onOpenCalendar, onPointsChange }) {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState({});

  const fetch = useCallback(async () => {
    try {
      const from = new Date();
      from.setHours(0, 0, 0, 0);
      const to = new Date();
      to.setDate(to.getDate() + 7);
      const { data } = await axios.get(
        `${API}/calendar/events?from_date=${from.toISOString()}&to_date=${to.toISOString()}`,
        { withCredentials: true }
      );
      setEvents(data.items || []);
      setCategories(data.categories || {});
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    fetch();
    const interval = setInterval(fetch, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, [fetch]);

  const now = new Date();
  const upcoming = events
    .filter((e) => !e.completed && new Date(e.starts_at) >= new Date(now.getTime() - 30 * 60000))
    .slice(0, 4);

  if (upcoming.length === 0) return null;

  const toggleComplete = async (ev) => {
    try {
      await axios.post(
        `${API}/calendar/events/complete`,
        { event_id: ev.event_id, occurrence_date: ev.occurrence_date },
        { withCredentials: true }
      );
      onPointsChange?.();
      await fetch();
    } catch { /* noop */ }
  };

  return (
    <div className="neo-card bg-gradient-to-br from-[#FFE8F0] via-[#F3E8FF] to-[#E8F0FF] p-4 border-[3px]" data-testid="upcoming-events-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <h3 className="font-bold font-['Fredoka'] text-base flex items-center gap-1.5">
          <CalendarDays className="w-4 h-4" /> Kommande händelser
        </h3>
        <button
          onClick={onOpenCalendar}
          className="text-[11px] font-bold flex items-center gap-0.5 opacity-70 hover:opacity-100"
          data-testid="upcoming-open-calendar"
        >
          Se kalender <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      <div className="space-y-1.5">
        {upcoming.map((ev, i) => {
          const c = categories[ev.category] || { color: '#eee', emoji: '•' };
          const startDt = new Date(ev.starts_at);
          const isSoon = (startDt - now) < 60 * 60 * 1000 && (startDt - now) > -30 * 60000;
          return (
            <div
              key={`${ev.event_id}-${ev.occurrence_date}-${i}`}
              className={`rounded-xl border-2 border-black p-2 flex items-center gap-2 ${isSoon ? 'ring-2 ring-pink-400 animate-pulse-soft' : ''}`}
              style={{ backgroundColor: c.color + '90' }}
              data-testid={`upcoming-event-${ev.event_id}-${ev.occurrence_date}`}
            >
              <button
                onClick={() => toggleComplete(ev)}
                className="w-6 h-6 rounded-full border-2 border-black bg-white flex items-center justify-center flex-shrink-0 hover:bg-[#B9FBC0]"
                title="Markera klar (+5 p)"
              >
                <Check className="w-3 h-3 opacity-0 hover:opacity-100" />
              </button>
              <span className="text-base">{c.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm leading-tight truncate flex items-center gap-1">
                  {ev.title}
                  {ev.reminder_minutes !== null && ev.reminder_minutes !== undefined && (
                    <Bell className="w-3 h-3 opacity-60 flex-shrink-0" />
                  )}
                </p>
                <p className="text-[11px] opacity-75 flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />
                  <span className="font-semibold">{relativeTime(ev.starts_at, now)}</span>
                  {!ev.all_day && <span className="opacity-70">· {fmtTime(ev.starts_at)}</span>}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
